import { Link, Navigate, NavLink, Outlet, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { WorkspaceProvider, useWorkspaceCampaignQuery } from "./WorkspaceContext";

// The fixed tab order from the CEO Rev 1.0 patch.
const TABS: ReadonlyArray<readonly [slug: string, label: string]> = [
  ["overview", "Overview"],
  ["events", "Events"],
  ["media", "Media"],
  ["deliverables", "Deliverables"],
  ["accomplishments", "Accomplishments"],
  ["reports", "Reports"],
  ["playlists", "Playlists"],
  ["team", "Team"],
  ["surveys", "Surveys"],
];

export function WorkspaceLayout() {
  const { campaignId = "" } = useParams();
  const { flags } = useAuth();
  const { data: campaign, isLoading, error } = useWorkspaceCampaignQuery(campaignId);

  // Deploy-dark gate: with the flag off, the workspace flow is invisible —
  // bounce any direct navigation back to the campaigns list.
  if (!flags.campaignWorkspaces) return <Navigate to="/admin/campaigns" replace />;

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading workspace…</div>;
  }
  if (error || !campaign) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          This campaign isn't available — it may not exist, or you don't have access to it.
        </p>
        <Link to="/admin/campaigns" className="text-electric text-sm hover:underline">
          ← All campaigns
        </Link>
      </div>
    );
  }

  return (
    <WorkspaceProvider campaign={campaign}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/campaigns"
            className="p-1.5 rounded-md hover:bg-foreground/5"
            aria-label="All campaigns"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="font-head text-2xl font-semibold">{campaign.name}</h1>
          <Badge variant={campaign.status === "active" ? "ok" : "muted"}>
            {campaign.status}
          </Badge>
        </div>
        <nav className="flex flex-wrap gap-1 border-b border-admin-rule">
          {TABS.map(([slug, label]) => (
            <NavLink
              key={slug}
              to={slug}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 text-sm font-medium border-b-2 -mb-px",
                  isActive
                    ? "border-gold text-gold"
                    : "border-transparent text-foreground/70 hover:text-foreground",
                )
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <Outlet />
      </div>
    </WorkspaceProvider>
  );
}
